'use client';

import { useEffect } from 'react';
import { cn } from '@coas/ui/lib';
import { sexSchema } from '@coas/validators/const';
import { useAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import { api } from 'trpc/react';
import Icon20LogoInstagramOutline from '~/shared/assets/icons/20-logo-instagram-outline.svg';
import Icon20LogoTelegram from '~/shared/assets/icons/20-logo-telegram.svg';
import IconClose from '~/shared/assets/icons/close.svg';
import { LocaleSwitcher } from '~/widgets/Header/ui/LocaleSwitcher';
import { sidebarStateAtom } from '../model/state';
import { CategoryLink, CategoryLinkSkeleton } from './CategoryLink';
import { SexLink } from './SexLink';
import styles from './Sidebar.module.scss';

export default () => {
    const [{ open, sex }, setState] = useAtom(sidebarStateAtom);
    const t = useTranslations('sidebar');
    const pathname = usePathname();
    // Read locale from raw URL prefix instead of useLocale() — useLocale() (next-intl context)
    // doesn't update inside an already-open ModalOverlay portal because the RSC tree above
    // doesn't re-render the modal subtree on locale switch. usePathname() is a client router
    // hook that fires on every URL change → key={locale} below remounts the dialog content.
    const locale = (pathname.startsWith('/ka') ? 'ka' : 'en') as 'en' | 'ka';

    // Auto-close on URL change. The NextIntlClientProvider context above an already-open
    // ModalOverlay portal doesn't re-render on locale switch, so the modal contents (tabs
    // via useTranslations, category names via pickLocalized) stay stale. Closing the sidebar
    // means the next open re-mounts the modal subtree from a freshly-rendered server tree.
    useEffect(() => {
        setState(prev => ({ ...prev, open: false }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    const { isLoading, isSuccess, data } = api.category.getAll.useQuery([sex]);

    return (
        <ModalOverlay
            className={styles.modalOverlay}
            isOpen={open}
            onOpenChange={() => setState(prev => ({ ...prev, open: false }))}
            isDismissable
        >
            <Modal className={styles.modal}>
                <Dialog key={locale} className={'min-h-screen w-full bg-neutral-50 text-neutral-950 '}>
                    <div
                        className={
                            'flex max-h-screen min-h-screen w-full flex-col gap-y-page overflow-auto p-page pt-0'
                        }
                    >
                        <div
                            className={
                                'sticky top-0 z-50 flex w-full items-center justify-center bg-neutral-50 pt-page'
                            }
                        >
                            <SexLink sex={sexSchema.enum.woman}>{t('women')}</SexLink>
                            <SexLink sex={sexSchema.enum.man}>{t('men')}</SexLink>
                            <button
                                type='button'
                                className={'ml-3 rounded-14 bg-neutral-200 p-2'}
                                onClick={() => setState(prev => ({ ...prev, open: false }))}
                            >
                                <IconClose className={'h-5 w-5 stroke-neutral-500'} />
                            </button>
                        </div>
                        <nav className={'flex flex-col gap-y-5'}>
                            {isLoading &&
                                Array.from({ length: 8 }).map((_, index) => <CategoryLinkSkeleton key={index} />)}
                            {isSuccess &&
                                data.items.map(category => <CategoryLink key={category.id} category={category} />)}
                        </nav>
                        <div className={'flex-1'} />
                        <div
                            className={cn(
                                'flex flex-col items-start',
                                'child:inline-flex child:items-center child:gap-x-1 child:py-1 child:text-neutral-600',
                            )}
                        >
                            <a href={'https://t.me/coasworld'} target={'_blank'} rel='noreferrer'>
                                <Icon20LogoTelegram className={'mb-1 size-4'} />
                                Telegram
                            </a>
                            <a
                                href={'https://www.instagram.com/coasworld?igsh=MXFkbXBobWthaDdwdw=='}
                                target={'_blank'}
                                rel='noreferrer'
                            >
                                <Icon20LogoInstagramOutline className={'mb-1 size-4'} />
                                Instagram
                            </a>
                            <div />
                            <Link href={'/docs/offer.pdf'} target={'_blank'}>
                                {t('offer')}
                            </Link>
                            <Link href={'/docs/personal.pdf'} target={'_blank'}>
                                {t('privacy')}
                            </Link>
                            <div />
                            <div className={'pt-2'}>
                                <span className={'mr-2 text-12 text-neutral-500'}>{t('language')}:</span>
                                <LocaleSwitcher />
                            </div>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
};
